// Pure period math for the Cycles of Life daily cycle.
// Ported from the companion Flutter app's CycleService so both clients
// agree on which period is active and when it starts and ends.
//
// Each day is divided into seven equal "slots" (~3h 25m 43s each) starting
// at local midnight. Every weekday begins on a different cycle letter, and
// the letters then advance one per slot, wrapping from G back to A.

const msPerDay = 86400 * 1000;
const msPerPeriod = msPerDay / 7;
const lastSlot = 6;

// First cycle index (A=0 … G=6) of each weekday, indexed by Date#getDay().
// Sun→G, Mon→C, Tue→F, Wed→B, Thu→E, Fri→A, Sat→D
export const weekdayStartPeriod = [6, 2, 5, 1, 4, 0, 3];

function startOfDay(date) {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate(), 0, 0, 0);
}

// Slot index (0–6) that `time` falls into within its local day.
// Clamped so a 25-hour DST fall-back day never yields slot 7.
export function getPeriodSlot(time) {
  const elapsed = time.getTime() - startOfDay(time).getTime();
  return Math.min(lastSlot, Math.floor(elapsed / msPerPeriod));
}

// Cycle index (A=0 … G=6) occupying `slot` on a weekday (Date#getDay() index).
export function getCycleForSlot(weekday, slot) {
  return (weekdayStartPeriod[weekday] + slot) % 7;
}

// Cycle index (A=0 … G=6) active at `time`.
export function getDailyCyclePeriod(time) {
  return getCycleForSlot(time.getDay(), getPeriodSlot(time));
}

// Inverse of getDailyCyclePeriod: which slot a given cycle occupies on a
// given weekday (Date#getDay() index). Each letter appears exactly once per day.
export function getSlotForCycle(cycle, weekday) {
  return (cycle - weekdayStartPeriod[weekday] + 7) % 7;
}

// Local midnight of the day in `date`'s Sunday–Saturday week that falls on
// `weekday` (Date#getDay() index).
export function dateOnWeekday(date, weekday) {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate() + weekday - date.getDay());
}

// Local start/end Date of `slot` on the calendar day containing `date`.
export function getPeriodRange(date, slot) {
  const dayStart = startOfDay(date);
  const start = new Date(dayStart.getTime() + slot * msPerPeriod);
  const end =
    slot >= lastSlot
      ? new Date(date.getFullYear(), date.getMonth(), date.getDate() + 1, 0, 0, 0)
      : new Date(dayStart.getTime() + (slot + 1) * msPerPeriod);
  return { start, end };
}

// 12-hour clock, e.g. "12:00 AM", "3:25 AM", "8:04 PM". Seconds are truncated.
export function formatTime(date) {
  const hours24 = date.getHours();
  const hours12 = hours24 % 12 === 0 ? 12 : hours24 % 12;
  const minutes = String(date.getMinutes()).padStart(2, '0');
  const suffix = hours24 >= 12 ? 'PM' : 'AM';
  return `${hours12}:${minutes} ${suffix}`;
}

// e.g. "12:00 AM – 3:25 AM"
export function formatPeriodRange(date, slot) {
  const { start, end } = getPeriodRange(date, slot);
  return `${formatTime(start)} – ${formatTime(end)}`;
}
